import { getAccessKey, isAccessKeyHandoff } from "@/lib/api/client";

export const RECOVERY_CODES_AT_RISK_STORAGE_KEY = "aether_codes_at_risk";
export const CODES_HOLD_RELEASED_STORAGE_KEY = "aether_codes_hold_released";

function fingerprint(token: string): string {
  let hash = 2166136261;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${(hash >>> 0).toString(16).padStart(8, "0")}:${token.length}`;
}

function removeStored(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    return;
  }
}

function storeFingerprint(key: string): void {
  const accessKey = getAccessKey();
  if (!accessKey) return;
  try {
    sessionStorage.setItem(key, fingerprint(accessKey));
  } catch {
    return;
  }
}

function matchesAccessKey(key: string): boolean {
  const accessKey = getAccessKey();
  if (!accessKey) return false;
  try {
    return sessionStorage.getItem(key) === fingerprint(accessKey);
  } catch {
    return false;
  }
}

export function persistRecoveryCodesAtRisk(): void {
  if (isAccessKeyHandoff()) return;
  removeStored(CODES_HOLD_RELEASED_STORAGE_KEY);
  storeFingerprint(RECOVERY_CODES_AT_RISK_STORAGE_KEY);
}

export function clearPersistedRecoveryCodesAtRisk(): void {
  removeStored(CODES_HOLD_RELEASED_STORAGE_KEY);
  removeStored(RECOVERY_CODES_AT_RISK_STORAGE_KEY);
}

export function readPersistedRecoveryCodesAtRisk(): boolean {
  const accessKey = getAccessKey();
  if (!accessKey) {
    clearPersistedRecoveryCodesAtRisk();
    return false;
  }
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(RECOVERY_CODES_AT_RISK_STORAGE_KEY);
  } catch {
    return false;
  }
  if (stored === fingerprint(accessKey)) return true;
  if (stored !== null && !isAccessKeyHandoff()) clearPersistedRecoveryCodesAtRisk();
  return false;
}

export function peekRecoveryCodesAtRisk(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_CODES_AT_RISK_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function recoveryCodesAtRiskBindingMatches(accessKey: string): boolean {
  if (!accessKey) return false;
  try {
    return sessionStorage.getItem(RECOVERY_CODES_AT_RISK_STORAGE_KEY) === fingerprint(accessKey);
  } catch {
    return false;
  }
}

export function rebindPersistedRecoveryCodesAtRisk(previousKey: string, nextKey: string): void {
  if (!previousKey || !nextKey) return;
  const previousFingerprint = fingerprint(previousKey);
  const nextFingerprint = fingerprint(nextKey);
  let atRisk: string | null;
  let hold: string | null;
  try {
    atRisk = sessionStorage.getItem(RECOVERY_CODES_AT_RISK_STORAGE_KEY);
    hold = sessionStorage.getItem(CODES_HOLD_RELEASED_STORAGE_KEY);
  } catch {
    return;
  }
  if (atRisk !== previousFingerprint) return;
  try {
    sessionStorage.setItem(RECOVERY_CODES_AT_RISK_STORAGE_KEY, nextFingerprint);
    if (hold === previousFingerprint) {
      sessionStorage.setItem(CODES_HOLD_RELEASED_STORAGE_KEY, nextFingerprint);
    }
  } catch {
    clearPersistedRecoveryCodesAtRisk();
  }
}

export function persistCodesHoldReleased(): void {
  if (isAccessKeyHandoff()) return;
  storeFingerprint(CODES_HOLD_RELEASED_STORAGE_KEY);
}

export function readPersistedCodesHoldReleased(): boolean {
  if (!readPersistedRecoveryCodesAtRisk()) {
    if (!isAccessKeyHandoff()) removeStored(CODES_HOLD_RELEASED_STORAGE_KEY);
    return false;
  }
  if (matchesAccessKey(CODES_HOLD_RELEASED_STORAGE_KEY)) return true;
  removeStored(CODES_HOLD_RELEASED_STORAGE_KEY);
  return false;
}
